import React, { useState, useEffect } from 'react';

// Popover that lets a user toggle column visibility and drag to reorder.
// Owns its own draft state; commits to the parent via onSave(newColumns).
// Theme tokens (c, s, isDark) come from the parent so the picker matches
// StaffPortal's existing visual language.
export default function ColumnPicker({ columns, onSave, onClose, c, s, isDark, disabled = false }) {
  const [draft, setDraft] = useState(columns);
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  // Reset draft if upstream columns change while the panel is open.
  useEffect(() => { setDraft(columns); }, [columns]);

  // `locked: true` columns can be reordered but never hidden — they carry the row's identity
  // (e.g. Assessor ID), so hiding one leaves a table nobody can read.
  function toggleVisible(key) {
    setDraft(prev => prev.map(col => (col.key === key && !col.locked) ? { ...col, visible: !col.visible } : col));
  }

  function moveItem(fromIdx, toIdx) {
    if (fromIdx === toIdx) return;
    setDraft(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setErrMsg('');
    try { await onSave(draft); }
    catch (e) { setErrMsg(e?.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{
      position: 'absolute',
      top: 'calc(100% + 6px)',
      right: 0,
      width: '260px',
      background: isDark ? 'var(--ink)' : '#ffffff',
      border: `1px solid ${c.border}`,
      borderRadius: '10px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      zIndex: 100,
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '10px 14px',
        borderBottom: `1px solid ${c.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: '11px', fontWeight: '700', color: c.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Columns</span>
        <button onClick={onClose}
          style={{ background: 'none', border: 'none', color: c.muted, fontSize: '14px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
      </div>
      <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
        {draft.map((col, i) => (
          <div key={col.key}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={e => e.preventDefault()}
            onDrop={() => { if (dragIdx !== null) moveItem(dragIdx, i); setDragIdx(null); }}
            onDragEnd={() => setDragIdx(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 14px',
              cursor: 'move',
              fontSize: '13px',
              color: c.text,
              opacity: dragIdx === i ? 0.4 : 1,
              borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6'}`,
              background: 'transparent',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = c.rowHover; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            <span style={{ color: c.muted, cursor: 'grab', userSelect: 'none', fontSize: '12px' }}>⋮⋮</span>
            <input
              type="checkbox"
              checked={col.locked ? true : col.visible !== false}
              disabled={!!col.locked}
              onChange={() => toggleVisible(col.key)}
              onClick={e => e.stopPropagation()}
              style={{ cursor: col.locked ? 'not-allowed' : 'pointer' }}
            />
            <span style={{ flex: 1 }}>{col.label}</span>
            {col.locked && <span title="Always shown" style={{ color: c.muted, fontSize: '10px', letterSpacing: '0.04em' }}>ALWAYS</span>}
          </div>
        ))}
      </div>
      {errMsg && (
        <div style={{ padding: '8px 14px', fontSize: '11px', color: '#f87171', borderTop: `1px solid ${c.border}` }}>{errMsg}</div>
      )}
      <div style={{ padding: '10px 14px', borderTop: `1px solid ${c.border}`, display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button onClick={onClose} disabled={saving}
          style={{ padding: '5px 12px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '6px', color: c.muted, fontSize: '12px', cursor: saving ? 'default' : 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving || disabled}
          style={{ padding: '5px 14px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: (saving || disabled) ? 'default' : 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", opacity: (saving || disabled) ? 0.6 : 1 }}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
